import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
  Stethoscope, CalendarDays, FileText, Receipt, ClipboardList, Activity, Image as ImageIcon, Filter,
} from "lucide-react";

const TYPE_META = {
  treatment:   { icon: Stethoscope,    label: "Trajtim",         color: "bg-[#3B6FA8] text-white" },
  appointment: { icon: CalendarDays,   label: "Takim",           color: "bg-[#EAE4D9] text-[#2C4C3B]" },
  note:        { icon: FileText,       label: "Shënim SOAP",     color: "bg-[#4A8B71]/15 text-[#2C4C3B]" },
  plan:        { icon: ClipboardList,  label: "Plan",            color: "bg-[#D4A24C]/20 text-[#8B6914]" },
  file:        { icon: ImageIcon,      label: "Skedar",          color: "bg-[#EAE4D9] text-[#5C5C5C]" },
  invoice:     { icon: Receipt,        label: "Faturë",          color: "bg-[#2C4C3B] text-[#FDFBF7]" },
  perio:       { icon: Activity,       label: "Perio",           color: "bg-[#A05A3B]/20 text-[#A05A3B]" },
};

const CATEGORY_DOT = {
  "Mbushje":         "#3B6FA8",
  "Endodonti":       "#C04848",
  "Protetikë":       "#C9963A",
  "Implantologji":   "#5A5A5A",
  "Kirurgji":        "#000000",
  "Periodontologji": "#A05A3B",
  "Ortodonci":       "#7B5EA8",
  "Higjenë":         "#4A9A6B",
  "Diagnostikë":     "#888888",
};

const SQ_WEEKDAYS = ["E diel", "E hënë", "E martë", "E mërkurë", "E enjte", "E premte", "E shtunë"];
const SQ_MONTHS = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];
const SQ_MONTHS_SHORT = ["Jan", "Shk", "Mar", "Pri", "Maj", "Qer", "Kor", "Gsh", "Sht", "Tet", "Nën", "Dhj"];

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, "0");
    const mon = SQ_MONTHS_SHORT[d.getMonth()];
    const yr = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${day} ${mon} ${yr} · ${hh}:${mm}`;
  } catch { return iso; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const wd = SQ_WEEKDAYS[d.getDay()];
    const day = String(d.getDate()).padStart(2, "0");
    const mon = SQ_MONTHS[d.getMonth()];
    const yr = d.getFullYear();
    return `${wd}, ${day} ${mon} ${yr}`;
  } catch { return iso; }
};

export default function PatientTimeline({ patientId }) {
  const { format } = useCurrency();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/patients/${patientId}/timeline`).then(({ data }) => {
      setEvents(data);
      setLoading(false);
    });
  }, [patientId]);

  const filtered = filter === "all" ? events : events.filter((e) => e.type === filter);

  // Group by day
  const grouped = filtered.reduce((acc, e) => {
    const day = e.date ? e.date.slice(0, 10) : "—";
    (acc[day] ||= []).push(e);
    return acc;
  }, {});

  // Stats
  const stats = events.reduce((acc, e) => { acc[e.type] = (acc[e.type] || 0) + 1; return acc; }, {});
  const totalCost = events
    .filter((e) => e.type === "treatment" && e.cost)
    .reduce((s, e) => s + (e.cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="patient-timeline">
      <div>
        <div className="label-eyebrow">Progress Notes</div>
        <h3 className="mt-1 text-2xl font-semibold tracking-tight">Historiku Kronologjik</h3>
        <p className="mt-1 text-sm text-[#5C5C5C]">Të gjitha veprimet klinike për këtë pacient — me datë dhe detaje.</p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Trajtime" value={stats.treatment || 0} />
        <Stat label="Takime" value={stats.appointment || 0} />
        <Stat label="Shënime SOAP" value={stats.note || 0} />
        <Stat label="Total i Faturuar" value={format(totalCost)} />
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-[#5C5C5C]" />
        <Chip label="Të gjitha" active={filter === "all"} onClick={() => setFilter("all")} count={events.length} />
        {Object.entries(TYPE_META).map(([k, m]) => (
          <Chip key={k} label={m.label} active={filter === k}
                onClick={() => setFilter(k)} count={stats[k] || 0} />
        ))}
      </div>

      {loading ? (
        <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]">Duke ngarkuar…</div>
      ) : filtered.length === 0 ? (
        <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]">Pa ngjarje për këtë filtër.</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([day, items]) => (
            <div key={day} data-testid={`tl-day-${day}`}>
              <div className="sticky top-0 bg-[#FDFBF7] py-2 z-10">
                <div className="label-eyebrow">{day === "—" ? "Pa datë" : fmtDate(day)}</div>
              </div>
              <ol className="relative border-l-2 border-[#E2DFD8] ml-3 mt-2 space-y-4">
                {items.map((e) => <Event key={`${e.type}-${e.id}`} e={e} />)}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Event({ e }) {
  const { format } = useCurrency();
  const meta = TYPE_META[e.type] || TYPE_META.treatment;
  const Icon = meta.icon;
  const catColor = e.category ? CATEGORY_DOT[e.category] : null;

  return (
    <li className="ml-6 relative" data-testid={`tl-${e.type}-${e.id}`}>
      <span className={`absolute -left-9 flex h-7 w-7 items-center justify-center rounded-full ${meta.color} ring-4 ring-[#FDFBF7]`}>
        <Icon size={13} />
      </span>
      <div className="surface-card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {catColor && <span className="inline-block h-3 w-3 rounded-sm" style={{ background: catColor }} />}
              <div className="font-semibold text-[#1A1A1A]">{e.title}</div>
              {e.tooth_number && (
                <span className="rounded bg-[#EAE4D9] px-2 py-0.5 text-xs font-mono text-[#2C4C3B]">
                  Dh. {e.tooth_number}
                </span>
              )}
              {e.surfaces?.length > 0 && (
                <span className="rounded bg-[#EAE4D9] px-2 py-0.5 text-xs font-mono text-[#5C5C5C]">
                  {e.surfaces.join("·")}
                </span>
              )}
              {e.filling_class && (
                <span className="rounded-full bg-[#3B6FA8] px-2 py-0.5 text-[10px] font-bold text-white">
                  Klasa {e.filling_class}{e.post_endo ? " · RC" : ""}
                </span>
              )}
              {e.status && (
                <span className="rounded-full bg-[#EAE4D9] px-2 py-0.5 text-[10px] font-medium text-[#2C4C3B] uppercase">
                  {e.status.replace("_", " ")}
                </span>
              )}
            </div>

            <div className="mt-1 text-xs text-[#5C5C5C]">
              {fmt(e.date)}
              {e.doctor && <> · {e.doctor}</>}
              {e.uploaded_by && <> · ngarkuar nga {e.uploaded_by}</>}
            </div>

            {e.description && <div className="mt-2 text-sm text-[#1A1A1A]">{e.description}</div>}

            {/* SOAP */}
            {e.type === "note" && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                {["subjective", "objective", "assessment", "plan"].map((k) => e[k] && (
                  <div key={k} className="rounded border border-[#E2DFD8] p-2">
                    <span className="label-eyebrow">{k[0].toUpperCase()}</span>
                    <div className="mt-0.5">{e[k]}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Invoice numbers */}
            {e.type === "invoice" && (
              <div className="mt-2 text-sm">
                <span className="font-mono">{format(e.total || 0)}</span> · paguar{" "}
                <span className="font-mono">{format(e.paid || 0)}</span>
              </div>
            )}

            {/* File info */}
            {e.type === "file" && (
              <div className="mt-2 text-xs text-[#5C5C5C]">
                {e.content_type} · {(e.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>

          {/* Right column: cost */}
          {(e.cost || e.total) > 0 && e.type !== "invoice" && (
            <div className="text-right">
              <div className="font-mono text-sm font-semibold text-[#2C4C3B]">
                {format(e.cost || e.total || 0)}
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function Stat({ label, value }) {
  return (
    <div className="surface-card p-4">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 text-xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function Chip({ label, active, onClick, count }) {
  return (
    <button onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              active ? "bg-[#2C4C3B] text-[#FDFBF7]" : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
            }`}>
      {label}
      {count !== undefined && <span className={`inline-block rounded-full px-1.5 text-[10px] ${active ? "bg-white/20" : "bg-[#EAE4D9]"}`}>{count}</span>}
    </button>
  );
}
